import {
  arrangeDay,
  formatTimeSl,
  nowTimeIn,
  type CheckinStatus,
  type LocalDate,
  type Occurrence,
} from '@planer/shared';
import {
  CalendarCheck,
  CalendarDays,
  ChevronDown,
  CloudOff,
  LayoutTemplate,
  List,
  Palmtree,
  Sparkles,
  Undo2,
} from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import { Fragment, useMemo } from 'react';
import { AnytimeList } from '../components/AnytimeList';
import { DayGrid } from '../components/DayGrid';
import { NowCard } from '../components/NowCard';
import { ProgressRing } from '../components/ProgressRing';
import { NowLine, TimelineItem } from '../components/TimelineItem';
import { WeekStrip } from '../components/WeekStrip';
import { setCheckin } from '../lib/actions';
import { useDayView } from '../lib/dayView';
import { longDate, relativeDayTitle, shortDate } from '../lib/format';
import { itemColor } from '../lib/colors';
import { useNow, useOnline, usePlan, useStreaks, useWeek, useZone } from '../lib/hooks';
import { useDesktop } from '../lib/media';
import { openSheet } from '../lib/sheets';
import { useOutbox } from '../lib/outbox';

interface TodayScreenProps {
  today: LocalDate;
  selected: LocalDate;
  onSelect: (date: LocalDate) => void;
  onOpen: (occ: Occurrence) => void;
  onQuickAdd: (text: string) => void;
  /** Tap on an empty slot in the calendar view. */
  onAddAt: (date: LocalDate, time: string) => void;
}

const EXAMPLES = [
  'Fitnes pon, sre in pet ob 6:00 90 min',
  'Kreatin vsak dan 7:00',
  'Spanje vsak dan 23:00',
];

export function TodayScreen({
  today,
  selected,
  onSelect,
  onOpen,
  onQuickAdd,
  onAddAt,
}: TodayScreenProps) {
  const [view, setView] = useDayView();
  const desktop = useDesktop();
  const plan = usePlan();
  const now = useNow();
  const zone = useZone();
  const online = useOnline();
  const pendingCount = useOutbox().size;
  const week = useWeek(selected, today);
  const dayAll = useMemo(() => week.byDate.get(selected) ?? [], [week.byDate, selected]);
  // Things without a time of day are listed apart ("Danes še"), not in the timeline.
  const dayOccs = useMemo(() => dayAll.filter((o) => !o.untimed), [dayAll]);
  const anytime = useMemo(() => dayAll.filter((o) => o.untimed), [dayAll]);
  const streaks = useStreaks();
  const progress = week.progress.get(selected);
  const isToday = selected === today;
  const isPast = selected < today;
  const nowIso = now.toISOString();

  const focus = useMemo(() => {
    if (!isToday) return null;
    const day = arrangeDay(dayOccs, now);
    const running =
      day.current.find((o) => o.requiresCheck && !week.status(o.key)) ?? day.current[0];
    if (running && (!running.requiresCheck || !week.status(running.key))) {
      return { occ: running, running: true };
    }
    const next = day.next ?? day.later[0];
    return next ? { occ: next, running: false } : running ? { occ: running, running: true } : null;
  }, [isToday, dayOccs, now, week]);

  /** Index before which the "now" line goes (today only). */
  const nowIndex = isToday ? dayOccs.findIndex((o) => o.start > nowIso) : -2;
  const status = (o: Occurrence) => week.status(o.key);
  const mark = (o: Occurrence, s: CheckinStatus | null) => {
    setCheckin(o, s, status(o));
  };
  const info = week.dayInfo.get(selected);
  // The holiday itself is shown in its own card.
  const allDay = (week.allDay.get(selected) ?? []).filter(
    (a) => !a.own || a.own.id !== info?.pause?.id,
  );
  const template = plan.data?.templates.find((t) => t.id === info?.templateId) ?? null;
  const hasTemplates = (plan.data?.templates.length ?? 0) > 0;

  const grid = (
    <DayGrid
      occs={dayOccs}
      sleep={info?.pause ? null : (week.sleep.get(selected) ?? null)}
      isToday={isToday}
      now={now}
      zone={zone}
      status={(key) => week.status(key)}
      onOpen={onOpen}
      onToggle={(o) => {
        mark(o, status(o) === 'done' ? null : 'done');
      }}
      onAddAt={(time) => {
        onAddAt(selected, time);
      }}
    />
  );
  // On a computer the timeline and the calendar sit side by side.
  const showGrid = !desktop && view === 'calendar';

  return (
    <div className="mx-auto w-full max-w-xl px-4 pt-safe pb-36 lg:grid lg:max-w-6xl lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-8 lg:px-8 lg:pt-8 lg:pb-12">
      <div className="flex flex-col gap-4">
        <header className="flex items-end justify-between gap-3 pt-2 lg:pt-0">
          <div className="min-w-0">
            <p className="text-[14px] font-medium text-muted first-letter:uppercase">
              {longDate(selected)}
            </p>
            <h1 className="text-[34px] leading-tight font-bold tracking-tight">
              {relativeDayTitle(selected, today)}
            </h1>
            {hasTemplates && (
              <button
                type="button"
                data-testid="day-template"
                onClick={() => {
                  openSheet({ type: 'day', date: selected });
                }}
                className="tap mt-1.5 inline-flex min-h-8 items-center gap-1.5 rounded-full py-1 pr-2.5 pl-2 text-[13px] font-semibold text-text-2 hover:brightness-95"
                style={{
                  background: template
                    ? `color-mix(in srgb, ${itemColor(template.color)} 16%, var(--surface))`
                    : 'var(--surface-2)',
                }}
              >
                <LayoutTemplate size={14} />
                {template ? template.name : 'Brez predloge'}
                {info?.pattern?.cycleWeeks === 2 && (
                  <span className="font-medium text-muted">
                    · teden {info.weekIndex === 0 ? 'A' : 'B'}
                  </span>
                )}
                {info?.period && (
                  <span className="font-medium text-muted">· {info.period.name}</span>
                )}
                <ChevronDown size={14} className="text-muted" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 pb-1">
            {!isToday && (
              <button
                type="button"
                className="tap flex min-h-10 items-center gap-1.5 rounded-full bg-accent-soft px-3.5 text-[14px] font-semibold text-accent-ink"
                onClick={() => {
                  onSelect(today);
                }}
              >
                <Undo2 size={15} /> Danes
              </button>
            )}
            {progress && progress.total > 0 && (
              <ProgressRing done={progress.done} total={progress.total} size={52} />
            )}
          </div>
        </header>

        {(!online || pendingCount > 0) && (
          <div
            className="flex items-center gap-2 rounded-2xl bg-mid-soft px-3.5 py-2.5 text-[14px] text-mid-ink"
            role="status"
          >
            <CloudOff size={16} className="shrink-0" />
            <span>
              {!online ? 'Brez povezave' : 'Sinhroniziram'}
              {pendingCount > 0 && (
                <span data-testid="pending-sync"> · {pendingCount} čaka na pošiljanje</span>
              )}
            </span>
          </div>
        )}

        <WeekStrip
          days={week.days}
          progress={week.soFar}
          today={today}
          selected={selected}
          onSelect={onSelect}
        />

        {isToday && (
          <NowCard
            now={now}
            focus={focus}
            status={focus ? status(focus.occ) : undefined}
            progress={progress}
            onStatus={mark}
            onOpen={onOpen}
          />
        )}

        {info?.pause && (
          <section
            aria-label="Plan počiva"
            data-testid="pause-card"
            className="card flex items-start gap-3.5 p-4"
            style={{
              background: `color-mix(in srgb, ${itemColor(info.pause.color)} 10%, var(--surface))`,
            }}
          >
            <span
              className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl text-on-status"
              style={{ background: itemColor(info.pause.color) }}
            >
              <Palmtree size={22} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[17px] font-semibold">{info.pause.title}</p>
              <p className="text-[14px] text-text-2">
                Plan počiva
                {info.pause.startDate !== info.pause.endDate &&
                  ` · ${shortDate(info.pause.startDate)} – ${shortDate(info.pause.endDate)}`}
                . Ponavljajoče aktivnosti so ustavljene, kar dodaš za ta dan, ostane.
              </p>
              {info.pause.notes && (
                <p className="mt-1 text-[14px] whitespace-pre-wrap text-text-2">
                  {info.pause.notes}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                if (info.pause) openSheet({ type: 'allday', event: info.pause });
              }}
              className="tap min-h-9 shrink-0 rounded-full bg-surface px-3 text-[13px] font-semibold text-accent-ink shadow-[var(--shadow-card)]"
            >
              Uredi
            </button>
          </section>
        )}

        {allDay.length > 0 && (
          <section
            aria-label="Celodnevno"
            className="flex flex-wrap gap-1.5"
            data-testid="all-day-strip"
          >
            {allDay.map((a) => {
              const inner = (
                <>
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: a.color }} />
                  <span className="truncate">{a.title}</span>
                  <span className="shrink-0 text-muted">· ves dan</span>
                </>
              );
              const cls =
                'inline-flex max-w-full items-center gap-1.5 rounded-full py-1.5 pr-3 pl-2.5 text-[13px] font-medium';
              const style = { background: `color-mix(in srgb, ${a.color} 16%, var(--surface))` };
              return a.own ? (
                <button
                  key={a.key}
                  type="button"
                  className={`tap ${cls}`}
                  style={style}
                  onClick={() => {
                    if (a.own) openSheet({ type: 'allday', event: a.own });
                  }}
                >
                  {inner}
                </button>
              ) : (
                <span key={a.key} className={cls} style={style} title={a.calendar}>
                  {inner}
                </span>
              );
            })}
          </section>
        )}

        <AnytimeList
          occs={anytime}
          status={status}
          streaks={streaks}
          past={isPast}
          onStatus={mark}
          onOpen={onOpen}
        />

        {week.isLoading ? (
          <div className="space-y-3" aria-label="Nalagam">
            {[0, 1, 2].map((i) => (
              <div key={i} className="card h-16 animate-pulse opacity-60" />
            ))}
          </div>
        ) : !week.hasItems ? (
          <EmptyDay hasItems={false} onQuickAdd={onQuickAdd} />
        ) : (
          <section aria-labelledby="timeline-title">
            <div className="mb-2 flex min-h-9 items-center justify-between gap-2 px-1">
              <h2 id="timeline-title" className="section-label">
                {showGrid ? 'Koledar' : isToday ? 'Časovnica' : isPast ? 'Kako je šlo' : 'Plan'}
              </h2>
              <div
                role="radiogroup"
                aria-label="Pogled dneva"
                className="flex rounded-full bg-surface-2 p-0.5 lg:hidden"
              >
                {(
                  [
                    ['timeline', 'Časovnica', <List key="l" size={15} />],
                    ['calendar', 'Koledar', <CalendarDays key="c" size={15} />],
                  ] as const
                ).map(([value, label, icon]) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={view === value}
                    onClick={() => {
                      setView(value);
                    }}
                    className={`tap flex min-h-9 items-center gap-1.5 rounded-full px-3 text-[13px] font-semibold transition-colors ${
                      view === value
                        ? 'bg-surface text-text shadow-[var(--shadow-card)]'
                        : 'text-muted'
                    }`}
                  >
                    {icon}
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {showGrid ? (
              grid
            ) : dayOccs.length === 0 ? (
              anytime.length > 0 ? null : (
                <EmptyDay hasItems onQuickAdd={onQuickAdd} />
              )
            ) : (
              <>
                {dayOccs.some((o) => o.requiresCheck) && (
                  <p className="mb-2 px-1 text-right text-[12px] text-muted">→ opravljeno · ← ni</p>
                )}
                <ul>
                  <AnimatePresence initial={false}>
                    {dayOccs.map((o, i) => (
                      <Fragment key={o.key}>
                        {i === nowIndex && <NowLine time={formatTimeSl(nowTimeIn(zone, now))} />}
                        <TimelineItem
                          occ={o}
                          streak={streaks[o.itemId]}
                          status={status(o)}
                          overdue={isPast || (isToday && o.end <= nowIso)}
                          last={i === dayOccs.length - 1 && nowIndex !== -1}
                          onStatus={(s) => {
                            mark(o, s);
                          }}
                          onOpen={() => {
                            onOpen(o);
                          }}
                        />
                      </Fragment>
                    ))}
                    {isToday && nowIndex === -1 && (
                      <NowLine key="now-end" time={formatTimeSl(nowTimeIn(zone, now))} />
                    )}
                  </AnimatePresence>
                </ul>
              </>
            )}
          </section>
        )}
      </div>
      {desktop && (
        <aside aria-label="Koledar dneva" className="hidden lg:block">
          <div className="sticky top-8">
            <p className="section-label mb-2 flex min-h-9 items-center px-1">Koledar</p>
            <div className="max-h-[calc(100dvh-7rem)] overflow-y-auto rounded-[var(--radius-card)]">
              {grid}
            </div>
          </div>
        </aside>
      )}
    </div>
  );
}

function EmptyDay({
  hasItems,
  onQuickAdd,
}: {
  hasItems: boolean;
  onQuickAdd: (text: string) => void;
}) {
  return (
    <section className="card flex flex-col items-center px-6 py-8 text-center">
      <span
        className="grid h-14 w-14 place-items-center rounded-2xl bg-accent-soft text-accent"
        aria-hidden
      >
        {hasItems ? <CalendarCheck size={28} /> : <Sparkles size={28} />}
      </span>
      <h2 className="mt-4 text-[19px] font-semibold">
        {hasItems ? 'Ta dan je prost' : 'Sestavi svoj prvi dan'}
      </h2>
      <p className="mt-1 max-w-xs text-[15px] text-muted">
        {hasItems
          ? 'Nič ni načrtovano. Uživaj ali dodaj kaj z gumbom +.'
          : 'Napiši na kratko, kot bi napisal sporočilo. Poskusi s primerom:'}
      </p>
      {!hasItems && (
        <div className="mt-5 flex w-full flex-col gap-2">
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              type="button"
              className="tap min-h-12 rounded-2xl bg-surface-2 px-4 text-left text-[15px] font-medium"
              onClick={() => {
                onQuickAdd(ex);
              }}
            >
              <span className="mr-2 text-accent">+</span>
              {ex}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
